import { useEffect } from 'react';
import { callDeepLink } from '@croquiscom/web2app';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';

import { checkZPayFunctionExists } from '@common/log-manager';
import { gotoLogin } from '@common/utils';
import { showBottomZigzinBannerAtom, useCatalogProductIdParams, usePdpBaseInfo } from '@domains/pdp-common';

import { useBottomFloatingLog } from '../log/useBottomFloatingLog';

const FLOATING_ZIGZIN_BANNER_HIDE_KEY = 'floating_zigzin_banner_hide';

interface FloatingZigzinBannerServiceProps {
  is_app: boolean;
  is_logged_in: boolean;
}

const FLOATING_ZIGZIN_BANNER_KEY = 'floating_zigzin_banner';
export const useFloatingZigzinBannerService = ({ is_app, is_logged_in }: FloatingZigzinBannerServiceProps) => {
  const params = useCatalogProductIdParams();
  const { product } = usePdpBaseInfo(params);
  const discount_banner_info = product.promotion_info?.discount_banner_info;
  const [showBottomZigzinBanner, setShowBottomZigzinBanner] = useAtom(showBottomZigzinBannerAtom);

  const { impressionZigzinBannerUBL, clickZigzinBannerUBL } = useBottomFloatingLog();

  useEffect(() => {
    let show_bottom_floating_banner = true;
    const bottom_floating_close_date = localStorage.getItem(FLOATING_ZIGZIN_BANNER_HIDE_KEY);

    if (bottom_floating_close_date) {
      const close_time = dayjs(Number(bottom_floating_close_date)).format('YYYY-MM-DD');
      show_bottom_floating_banner = dayjs().isAfter(close_time, 'day');
    }

    setShowBottomZigzinBanner(!!discount_banner_info && show_bottom_floating_banner);
  }, [discount_banner_info]);

  useEffect(() => {
    if (sessionStorage.getItem(FLOATING_ZIGZIN_BANNER_KEY)) {
      sessionStorage.removeItem(FLOATING_ZIGZIN_BANNER_KEY);
      openFullScreenSimpleBrowser(discount_banner_info?.landing_url ?? '');
    }
  }, [discount_banner_info?.landing_url]);

  const handleLinkButton = () => {
    if (!is_logged_in) {
      if (is_app) {
        sessionStorage.setItem(FLOATING_ZIGZIN_BANNER_KEY, 'true');
        callDeepLink('/login')();
        return;
      }
      gotoLogin({ redirect_url: discount_banner_info?.landing_url ?? '' });
      return;
    }

    if (discount_banner_info?.landing_url) {
      clickZigzinBannerUBL(product.id, discount_banner_info.landing_url, discount_banner_info?.text ?? '');
      openFullScreenSimpleBrowser(discount_banner_info.landing_url);
    }
  };

  const handleCloseButton = () => {
    localStorage.setItem(FLOATING_ZIGZIN_BANNER_HIDE_KEY, new Date().getTime().toString());
    setShowBottomZigzinBanner(false);
  };

  const handleImpressionZigzinBanner = () => {
    impressionZigzinBannerUBL(product.id, discount_banner_info?.landing_url ?? '', discount_banner_info?.text ?? '');
  };

  return {
    showBottomZigzinBanner,
    discount_banner_info,
    handleLinkButton,
    handleCloseButton,
    handleImpressionZigzinBanner,
  };
};

const openFullScreenSimpleBrowser = (url: string) => {
  if (checkZPayFunctionExists(window)) {
    callDeepLink('/full_screen_simple_browser')({
      url: encodeURIComponent(url),
    });
  } else {
    window.open(url, '_blank');
  }
};
